/**
 * PrivateLoadMapSection
 * Map wrapper for load-hub expanded rows. Uses shared LeafletRouteMap with toll route support.
 * Geocodes through main-service (`@/components/geocode`) when coordinates are missing.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { CanonicalLoad, LoadStop } from '@numeo/types-core'
import { useColorScheme } from '@mui/joy/styles'
import { LeafletRouteMap, LatLng, Waypoint } from '@/components/LeafletRouteMap'
import { geocodeCityState } from '@/components/geocode'
import { LocationData } from '../../types/filters'
import { useTollRoutes } from '../../hooks/useTollRoutes'
import { TollRouteControls } from './TollRouteControls'
import { ConvoyDetailStop } from '../../api/convoy.api'

// ─── Geocoding ───────────────────────────────────────────────────────────────
// `geocodeCityState` (`@/components/geocode`) asks main-service; lookups fired together go as one batch.

/** Haversine distance in miles */
function haversineDistanceMiles(a: LatLng, b: LatLng): number {
    const R = 3958.8
    const dLat = ((b.lat - a.lat) * Math.PI) / 180
    const dLng = ((b.lng - a.lng) * Math.PI) / 180
    const sinLat = Math.sin(dLat / 2)
    const sinLng = Math.sin(dLng / 2)
    const h = sinLat * sinLat + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * sinLng * sinLng
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

// ─── Component ───────────────────────────────────────────────────────────────

interface PrivateLoadMapSectionProps {
    load: CanonicalLoad
    originLocation?: LocationData | null
    onLoadUpdate?: (updatedLoad: CanonicalLoad) => void
    /** Stops from detail API (Uber Freight, Emerge, Beon, etc.) */
    detailStops?: LoadStop[] | null
    /** Convoy-specific stops from OfferPage detail */
    convoyStops?: ConvoyDetailStop[] | null
    /** Hide the "Get Tolls" overlay control (default: shown). Opt-in for surfaces that don't want it, e.g. the
     *  box-truck load-detail thumbnail. Load-hub omits it, so its behavior is unchanged. */
    hideTolls?: boolean
}

/**
 * Extract intermediate waypoints from stops arrays.
 * Skips first pickup (= origin marker) and last delivery (= destination marker).
 * Returns waypoints with coordinates or city/state for geocoding.
 */
function extractWaypoints(
    loadStops: LoadStop[] | null | undefined,
    detailStops: LoadStop[] | null | undefined,
    convoyStops: ConvoyDetailStop[] | null | undefined
): Array<{ lat?: number | null; lng?: number | null; city: string; state: string; label: string }> {
    // Priority: detailStops > convoyStops > load.stops
    if (detailStops && detailStops.length > 2) {
        // Sort by stopSequence, skip first and last
        const sorted = [...detailStops].sort((a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0))
        return sorted.slice(1, -1).map((s, i) => ({
            lat: s.stopLat,
            lng: s.stopLon,
            city: s.stopCity ?? '',
            state: s.stopStateProv ?? '',
            label: `${i + 2}`,
        }))
    }

    if (convoyStops && convoyStops.length > 2) {
        const sorted = [...convoyStops].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
        return sorted.slice(1, -1).map((s, i) => ({
            lat: s.coordinates?.latitude,
            lng: s.coordinates?.longitude,
            city: s.address?.city ?? '',
            state: s.address?.state ?? '',
            label: `${i + 2}`,
        }))
    }

    if (loadStops && loadStops.length > 2) {
        const sorted = [...loadStops].sort((a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0))
        return sorted.slice(1, -1).map((s, i) => ({
            lat: s.stopLat,
            lng: s.stopLon,
            city: s.stopCity ?? '',
            state: s.stopStateProv ?? '',
            label: `${i + 2}`,
        }))
    }

    return []
}

export const PrivateLoadMapSection: React.FC<PrivateLoadMapSectionProps> = ({ load, originLocation, onLoadUpdate, detailStops, convoyStops, hideTolls }) => {
    const { mode } = useColorScheme()
    const isDark = mode === 'dark'

    const originCity = load.pickupCity ?? ''
    const originState = load.pickupStateProv ?? ''
    const destCity = load.deliveryCity ?? ''
    const destState = load.deliveryStateProv ?? ''

    const dbOrigin = useMemo(
        () => (load.pickupLat != null && load.pickupLon != null ? { lat: load.pickupLat, lng: load.pickupLon } : null),
        [load.pickupLat, load.pickupLon]
    )
    const dbDest = useMemo(
        () => (load.deliveryLat != null && load.deliveryLon != null ? { lat: load.deliveryLat, lng: load.deliveryLon } : null),
        [load.deliveryLat, load.deliveryLon]
    )

    const [geocodedCoords, setGeocodedCoords] = useState<{ origin: LatLng | null; dest: LatLng | null; searchOrigin: LatLng | null }>({
        origin: null,
        dest: null,
        searchOrigin: null,
    })
    const [isGeocoding, setIsGeocoding] = useState(false)
    const fetchedRef = useRef(false)
    const mountedRef = useRef(true)
    const deadheadUpdatedRef = useRef(false)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    // Search origin — from coordinates or needs geocoding
    const searchOriginDirect: LatLng | null = useMemo(
        () => (originLocation?.latitude && originLocation?.longitude ? { lat: originLocation.latitude, lng: originLocation.longitude } : null),
        [originLocation?.latitude, originLocation?.longitude]
    )
    const searchOriginCity = originLocation?.city ?? ''
    const searchOriginState = originLocation?.stateProv ?? ''

    // Geocode missing coordinates
    useEffect(() => {
        if (fetchedRef.current) return
        fetchedRef.current = true

        const needsOrigin = !dbOrigin && originCity
        const needsDest = !dbDest && destCity
        const needsSearchOrigin = !searchOriginDirect && searchOriginCity

        if (!needsOrigin && !needsDest && !needsSearchOrigin) return

        setIsGeocoding(true)
        Promise.all([
            needsOrigin ? geocodeCityState(originCity, originState) : null,
            needsDest ? geocodeCityState(destCity, destState) : null,
            needsSearchOrigin ? geocodeCityState(searchOriginCity, searchOriginState) : null,
        ])
            .then(([o, d, s]) => {
                if (mountedRef.current) setGeocodedCoords({ origin: o, dest: d, searchOrigin: s })
            })
            .finally(() => {
                if (mountedRef.current) setIsGeocoding(false)
            })
    }, [dbOrigin, dbDest, originCity, originState, destCity, destState, searchOriginDirect, searchOriginCity, searchOriginState])

    const finalOrigin = useMemo(() => dbOrigin ?? geocodedCoords.origin ?? { lat: 0, lng: 0 }, [dbOrigin, geocodedCoords.origin])
    const finalDest = useMemo(() => dbDest ?? geocodedCoords.dest ?? { lat: 0, lng: 0 }, [dbDest, geocodedCoords.dest])
    const hasFinalCoords = finalOrigin.lat !== 0 && finalOrigin.lng !== 0 && finalDest.lat !== 0 && finalDest.lng !== 0

    const searchOriginLatLng: LatLng | null = searchOriginDirect ?? geocodedCoords.searchOrigin

    // Calculate deadhead miles when search origin is available
    useEffect(() => {
        if (!searchOriginLatLng || !hasFinalCoords || deadheadUpdatedRef.current) return
        if (load.details?.origin_deadhead_miles != null || !onLoadUpdate) return
        deadheadUpdatedRef.current = true
        const dhMiles = Math.round(haversineDistanceMiles(searchOriginLatLng, finalOrigin) * 100) / 100
        onLoadUpdate({
            ...load,
            details: {
                driverShouldUnload: null,
                ...load.details,
                origin_deadhead_miles: dhMiles,
                destination_deadhead_miles: load.details?.destination_deadhead_miles ?? null,
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchOriginLatLng, hasFinalCoords])

    // Extract intermediate waypoints from stops (skip origin + destination)
    const rawWaypoints = useMemo(() => extractWaypoints(load.stops, detailStops, convoyStops), [load.stops, detailStops, convoyStops])

    // Geocode waypoints that lack coordinates
    const [resolvedWaypoints, setResolvedWaypoints] = useState<Waypoint[]>([])
    useEffect(() => {
        if (rawWaypoints.length === 0) {
            setResolvedWaypoints([])
            return
        }
        let cancelled = false
        const resolve = async () => {
            const resolved = await Promise.all(
                rawWaypoints.map(async (wp): Promise<Waypoint | null> => {
                    if (wp.lat && wp.lng) return { lat: wp.lat, lng: wp.lng, label: wp.label }
                    if (wp.city) {
                        const coords = await geocodeCityState(wp.city, wp.state)
                        if (coords) return { lat: coords.lat, lng: coords.lng, label: wp.label }
                    }
                    return null
                })
            )
            if (!cancelled) setResolvedWaypoints(resolved.filter((w): w is Waypoint => w !== null))
        }
        void resolve()
        return () => {
            cancelled = true
        }
    }, [rawWaypoints])

    // Toll routes
    const tollState = useTollRoutes({
        origin: { lat: finalOrigin.lat, lng: finalOrigin.lng, city: originCity, state: originState },
        destination: { lat: finalDest.lat, lng: finalDest.lng, city: destCity, state: destState },
        hasFinalCoords,
    })

    // Google Maps link
    const searchOriginLabel = searchOriginCity && searchOriginState ? `${searchOriginCity}, ${searchOriginState}` : ''
    const gmapWaypoints = searchOriginLabel ? `&waypoints=${encodeURIComponent(`${originCity}, ${originState}`)}` : ''
    const gmapOrigin = searchOriginLabel || `${originCity}, ${originState}`
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(gmapOrigin)}&destination=${encodeURIComponent(`${destCity}, ${destState}`)}&travelmode=driving${gmapWaypoints}`

    if (isGeocoding) {
        return (
            <div className="flex items-center justify-center h-full min-h-[200px] flex-col gap-1">
                <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-[10px] text-gray-400">Finding coordinates...</span>
            </div>
        )
    }

    if (!hasFinalCoords) {
        return (
            <div className="flex items-center justify-center h-full min-h-[200px] flex-col gap-1">
                <span className="text-xs text-gray-400">Route unavailable</span>
            </div>
        )
    }

    return (
        <div className="relative w-full h-full min-h-[200px]">
            <LeafletRouteMap
                origin={finalOrigin}
                destination={finalDest}
                waypoints={resolvedWaypoints.length > 0 ? resolvedWaypoints : undefined}
                userSearchedOrigin={searchOriginLatLng}
                isDark={isDark}
                routes={tollState.showTollBoxes ? tollState.tollRoutes : undefined}
                selectedRouteIndex={tollState.selectedRouteIndex}
            />

            {/* Toll controls overlay (opt-out via hideTolls) */}
            {!hideTolls && (
                <TollRouteControls
                    tollRoutes={tollState.tollRoutes}
                    selectedRouteIndex={tollState.selectedRouteIndex}
                    isTollLoading={tollState.isTollLoading}
                    showTollBoxes={tollState.showTollBoxes}
                    tollError={tollState.tollError}
                    origin={{ city: originCity, state: originState }}
                    destination={{ city: destCity, state: destState }}
                    onGetTolls={tollState.handleGetTolls}
                    onTollBoxClick={tollState.handleTollBoxClick}
                    onHideTolls={tollState.hideTollBoxes}
                />
            )}

            {/* Google Maps link (hidden when toll boxes are showing — Details button replaces it) */}
            {!tollState.showTollBoxes && (
                <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    // Map chrome follows the same dark treatment as Leaflet's own controls
                    // (`.dark .leaflet-bar` in styles/index.css) so this button doesn't pop as a
                    // bright rectangle over the CARTO dark basemap.
                    className="absolute bottom-2 right-2 z-[500] bg-white hover:bg-gray-50 rounded p-1.5 shadow-sm border border-gray-200 dark:bg-[var(--lh-surface)] dark:hover:bg-[var(--lh-surface-secondary)] dark:border-[var(--lh-border)]"
                >
                    <svg className="h-4 w-4 text-gray-600 dark:text-[var(--lh-fg)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                        />
                    </svg>
                </a>
            )}
        </div>
    )
}
